import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { apiUrl } from '../../utils/apiBase';
import { isTrendyolProductMatched } from '../../utils/trendyolListingMatch';

/**
 * Trendyol Ürün Detay modali içinde mağaza kataloğunda arayıp yerel ürünle eşleştirir.
 */
export default function TrendyolProductMatchingPanel({
  subeId,
  productId,
  /** editForm ile mevcut eşleştirme alanı eş zamanlı okunur (örn. ty_matched*) */
  productSnapshot,
  /** async () => headers */
  getAuthHeaders,
  /** Firestore kaydı başarıyla yazıldıktan sonra üst liste form state’inin güncellenmesi için */
  onMatchApplied = null
}) {
  const snap = productSnapshot && typeof productSnapshot === 'object' ? productSnapshot : {};
  const isMatched = isTrendyolProductMatched(snap);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [hints, setHints] = useState([]);
  const [savingId, setSavingId] = useState('');

  const runSearch = async () => {
    const q = String(query || '').trim();
    setError('');
    setHints([]);
    if (!subeId || subeId === 'Bilinmeyen Şube') {
      setError('Şube bilgisi yok.');
      return;
    }
    if (!productId) {
      setError('Ürün kimliği bulunamadı.');
      return;
    }
    if (q.length < 2) {
      setError('Aramak için en az 2 karakter girin (barkod, stok kodu veya ürün adı).');
      return;
    }

    setLoading(true);
    setResults([]);
    try {
      const url = `${apiUrl('/api/trendyol/products/search')}?q=${encodeURIComponent(q)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(await getAuthHeaders())
        }
      });
      const parsed = await response.json().catch(() => ({}));
      if (!response.ok || parsed?.success !== true) {
        throw new Error(parsed?.message || 'Trendyol araması başarısız');
      }
      setResults(Array.isArray(parsed.results) ? parsed.results : []);
      setHints(Array.isArray(parsed.hints) ? parsed.hints : []);
    } catch (e) {
      setError(e?.message || 'Arama sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const applyMatch = async (row) => {
    if (!row?.matchedProductId || !subeId || subeId === 'Bilinmeyen Şube' || !productId) return;
    setError('');
    setSavingId(row.matchedProductId);
    try {
      const ref = doc(db, 'subeler', subeId, 'urunler', productId);
      const urlStr = row.productUrl ? String(row.productUrl).trim() : '';
      const tyBarcode =
        row.barcode != null && String(row.barcode).trim() !== '' ? String(row.barcode).trim() : null;
      const tyStockCode =
        row.stockCode != null && String(row.stockCode).trim() !== '' ? String(row.stockCode).trim() : null;
      const platformListingId =
        row.platformListingId != null && String(row.platformListingId).trim() !== ''
          ? String(row.platformListingId).trim()
          : null;
      const productMainIdTy =
        row.productMainId != null && String(row.productMainId).trim() !== ''
          ? String(row.productMainId).trim()
          : null;

      const docPayload = {
        ty_syncMode: 'matched',
        ty_matchedProductId: String(row.matchedProductId),
        ty_matchedBarcode: tyBarcode,
        ty_matchedStockCode: tyStockCode,
        ty_matchedProductName: row.title ? String(row.title) : null,
        ty_matchedProductUrl: urlStr ? urlStr : null,
        ty_matchedAt: serverTimestamp(),
        ty_matchedSource: 'manual'
      };
      if (tyBarcode) docPayload.ty_barcode = tyBarcode;
      if (tyStockCode) docPayload.ty_stockCode = tyStockCode;
      if (urlStr) {
        docPayload.ty_productUrl = urlStr;
      }
      if (platformListingId) docPayload.ty_platformListingId = platformListingId;
      if (productMainIdTy) docPayload.ty_productMainId = productMainIdTy;

      await updateDoc(ref, docPayload);
      const localPatch = {
        ty_syncMode: 'matched',
        ty_matchedProductId: String(row.matchedProductId),
        ty_matchedBarcode: tyBarcode || '',
        ty_matchedStockCode: tyStockCode || '',
        ty_matchedProductName: row.title ? String(row.title) : '',
        ty_matchedProductUrl: urlStr || '',
        ty_productUrl: urlStr || '',
        ty_matchedSource: 'manual'
      };
      if (tyBarcode) localPatch.ty_barcode = tyBarcode;
      if (tyStockCode) localPatch.ty_stockCode = tyStockCode;
      if (platformListingId) localPatch.ty_platformListingId = platformListingId;
      if (productMainIdTy) localPatch.ty_productMainId = productMainIdTy;
      onMatchApplied?.(localPatch);
    } catch (e) {
      setError(e?.message || 'Eşleştirme kaydı yazılamadı.');
    } finally {
      setSavingId('');
    }
  };

  const matchedAlert = (
    <Alert
      severity="success"
      icon={<LinkOutlinedIcon fontSize="inherit" />}
      sx={{ borderRadius: 2.5, border: '1px solid #bbf7d0', bgcolor: '#ecfdf5' }}
    >
      <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: '#14532d' }}>
        Bu ürün Trendyol’da mevcut bir listeyle eşleştirildi.
      </Typography>
      {snap.ty_matchedProductName ? (
        <Typography sx={{ mt: 0.6, fontSize: '0.82rem', color: '#166534', fontWeight: 700 }}>
          {String(snap.ty_matchedProductName)}
        </Typography>
      ) : null}
      <Typography variant="caption" component="div" sx={{ mt: 0.5, display: 'block', color: '#15803d' }}>
        Liste kimliği: {snap.ty_matchedProductId || '—'} · Barkod: {snap.ty_matchedBarcode || snap.ty_barcode || '—'} · Stok
        kodu: {snap.ty_matchedStockCode || snap.ty_stockCode || '—'}
      </Typography>
      <Typography variant="caption" component="div" sx={{ mt: 0.35, display: 'block', color: '#166534' }}>
        Yanlış eşleşme veya barkod güncellemesi için aşağıdan yeniden arayıp tekrar Eşleştir seçebilirsiniz.
      </Typography>
      {snap.ty_matchedProductUrl ? (
        <Link href={snap.ty_matchedProductUrl} target="_blank" rel="noopener noreferrer" sx={{ mt: 0.75, display: 'inline-block', fontWeight: 700 }}>
          Trendyol ürün bağlantısı
        </Link>
      ) : null}
    </Alert>
  );

  const searchChrome = (
    <Stack spacing={1.35}>
      <Typography sx={{ fontWeight: 850, fontSize: '0.92rem', color: '#92400e' }}>Trendyol’da ara</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: '#78350f', lineHeight: 1.42 }}>
        Satıcı kataloğunuzda barkod veya stok kodu doğrudan sorgulanır; ürün adı ise sınırlı sayfa içinde başlıkta aranır. Sonuçlardan doğru olan satır için Eşleştir’i kullanın.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          fullWidth
          size="small"
          label="Barkod, stok kodu veya Trendyol adı"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          sx={{ bgcolor: '#fff' }}
        />
        <Button
          variant="contained"
          onClick={runSearch}
          disabled={loading || !productId || !subeId || subeId === 'Bilinmeyen Şube'}
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: '#c2410c',
            '&:hover': { bgcolor: '#9a3412' },
            flexShrink: 0,
            px: 2
          }}
        >
          Ara
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      {hints.length ? (
        <Typography variant="caption" sx={{ color: '#92400e' }}>
          {hints.join(' ')}
        </Typography>
      ) : null}
      {!loading && results.length > 0 ? (
        <Table size="small" sx={{ border: '1px solid #fdd8b2', borderRadius: 2, '& .MuiTableCell-root': { fontSize: '0.78rem' } }}>
          <TableHead sx={{ bgcolor: '#fffbeb' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 850 }}>Ad</TableCell>
              <TableCell sx={{ fontWeight: 850 }}>Barkod</TableCell>
              <TableCell sx={{ fontWeight: 850 }}>Stok</TableCell>
              <TableCell align="right" sx={{ fontWeight: 850 }}>
                İşlem
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row) => {
              const sid = uniqRowKey(row);
              return (
                <TableRow key={sid}>
                  <TableCell sx={{ maxWidth: 240, overflowWrap: 'anywhere', fontWeight: 700 }}>
                    {row.title || '—'}
                  </TableCell>
                  <TableCell>{row.barcode || '—'}</TableCell>
                  <TableCell>{row.stockCode || '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={Boolean(savingId)}
                      sx={{ fontWeight: 800, textTransform: 'none' }}
                      onClick={() => applyMatch(row)}
                    >
                      {savingId === row.matchedProductId ? <CircularProgress size={16} /> : 'Eşleştir'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
    </Stack>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2.5,
        border: '1px solid #fdba74',
        bgcolor: '#fff7ed'
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: '#9a3412', fontSize: '1rem' }}>Trendyol eşleştirme</Typography>
          <Typography sx={{ color: '#78350f', fontSize: '0.82rem', mt: 0.35, lineHeight: 1.42 }}>
            Daha önce Trendyol’da açılmış listeyle bu kasa kaydını ilişkilendirin. Bu aşama yeni ürün oluşturmaz.
          </Typography>
        </Box>
        {isMatched ? matchedAlert : null}
        {searchChrome}
      </Stack>
    </Paper>
  );
}

function uniqRowKey(row) {
  return `${row.matchedProductId || ''}_${row.barcode || ''}_${row.stockCode || ''}`;
}
